import React from "react";
import { AlertCircle, RefreshCw } from "lucide-react";

/**
 * Reusable basic shimmer box
 */
export function SkeletonBox({ className = "", style = {} }) {
  return <div className={`skeleton ${className}`} style={style} />;
}

/**
 * Reusable skeleton card container
 */
export function SkeletonCard({ className = "", children }) {
  return (
    <div className={`card p-6 ${className}`}>
      {children}
    </div>
  );
}

/**
 * Reusable row skeleton mimicking transaction & activity rows
 * Layout: Circle on left + two stacked lines in middle + number on right
 */
export function SkeletonRow({ className = "" }) {
  return (
    <div className={`min-h-[72px] py-3.5 px-4 flex items-center justify-between gap-3 border-b border-[var(--border)] last:border-b-0 ${className}`}>
      {/* Circle on left (40px) */}
      <SkeletonBox className="w-10 h-10 rounded-full shrink-0" />
      {/* Two text lines in middle */}
      <div className="flex-1 min-w-0 space-y-2">
        <SkeletonBox className="h-4 w-36 max-w-[65%]" />
        <SkeletonBox className="h-3 w-52 max-w-[85%]" />
      </div>
      {/* Right side number/badge */}
      <div className="shrink-0 flex flex-col items-end space-y-1.5 min-w-[70px]">
        <SkeletonBox className="h-4.5 w-16" />
        <SkeletonBox className="h-2.5 w-10" />
      </div>
    </div>
  );
}

/**
 * Dashboard page skeleton matching exact layout:
 * - 5 KPI cards in a horizontal scroll row
 * - 1 alert banner bar
 * - 2 charts side by side
 * - 2 property cards
 * - 6 activity feed rows
 */
export function DashboardSkeleton() {
  return (
    <div className="animate-in fade-in duration-200" data-testid="dashboard-skeleton">
      {/* 5 KPI Cards Row */}
      <div className="mb-10">
        <div className="flex md:grid md:grid-cols-3 lg:grid-cols-5 gap-4 overflow-x-auto no-scrollbar pb-2">
          {[1, 2, 3, 4, 5].map((i) => (
            <div
              key={i}
              className="card min-w-[170px] sm:min-w-0 min-h-[160px] p-6 flex flex-col justify-between"
            >
              <div className="flex justify-between items-center">
                <SkeletonBox className="h-3 w-20" />
                <SkeletonBox className="w-8 h-8 rounded-xl" />
              </div>
              <SkeletonBox className="h-8 w-28 my-3" />
              <SkeletonBox className="h-3 w-24" />
            </div>
          ))}
        </div>
      </div>

      {/* Alert Banner Area */}
      <div className="mb-10 card min-h-[56px] px-4 py-3 border-l-4 border-slate-700/50 rounded-xl flex items-center justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0 flex-1">
          <SkeletonBox className="w-8 h-8 rounded-lg shrink-0" />
          <div className="space-y-1.5 flex-1">
            <SkeletonBox className="h-4 w-48 max-w-[60%]" />
            <SkeletonBox className="h-3 w-72 max-w-[80%]" />
          </div>
        </div>
        <SkeletonBox className="h-8 w-28 rounded-lg shrink-0" />
      </div>

      {/* Charts Area: 2 Rectangles Side by Side */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-10">
        <div className="card p-6 min-h-[340px] flex flex-col justify-between">
          <div className="flex justify-between items-center mb-4">
            <SkeletonBox className="h-5 w-44" />
            <SkeletonBox className="h-4 w-20" />
          </div>
          <SkeletonBox className="h-60 w-full rounded-xl" />
        </div>
        <div className="card p-6 min-h-[340px] flex flex-col justify-between">
          <div className="flex justify-between items-center mb-4">
            <SkeletonBox className="h-5 w-44" />
            <SkeletonBox className="h-4 w-20" />
          </div>
          <SkeletonBox className="h-60 w-full rounded-xl" />
        </div>
      </div>

      {/* Property Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-10">
        {[1, 2].map((i) => (
          <div key={i} className="card p-6 flex flex-col justify-between">
            <SkeletonBox className="h-40 w-full rounded-xl mb-4" />
            <SkeletonBox className="h-5 w-52 mb-2" />
            <SkeletonBox className="h-3.5 w-32" />
          </div>
        ))}
      </div>

      {/* Activity Feed: 5-6 Rows */}
      <div className="card divide-y divide-[var(--border)] overflow-hidden">
        <div className="p-4 border-b border-[var(--border)]">
          <SkeletonBox className="h-5 w-36" />
        </div>
        {[1, 2, 3, 4, 5, 6].map((i) => (
          <SkeletonRow key={i} />
        ))}
      </div>
    </div>
  );
}

/**
 * Transactions page skeleton:
 * - Search bar shape
 * - Filter chips (3-4 pill shapes)
 * - 6 transaction rows
 */
export function TransactionsSkeleton() {
  return (
    <div className="animate-in fade-in duration-200" data-testid="transactions-skeleton">
      {/* Search Bar Skeleton */}
      <div className="mb-4">
        <SkeletonBox className="h-11 w-full rounded-xl" />
      </div>

      {/* Category Filter Chips Skeleton */}
      <div className="flex items-center gap-2 mb-4 overflow-x-auto no-scrollbar pb-1">
        <SkeletonBox className="h-8 w-28 rounded-full shrink-0" />
        <SkeletonBox className="h-8 w-32 rounded-full shrink-0" />
        <SkeletonBox className="h-8 w-24 rounded-full shrink-0" />
        <SkeletonBox className="h-8 w-36 rounded-full shrink-0" />
      </div>

      {/* Type Toggle Skeleton */}
      <div className="mb-6">
        <SkeletonBox className="h-10 w-64 rounded-xl" />
      </div>

      {/* 6 Transaction Rows */}
      <div className="card divide-y divide-[var(--border)] overflow-hidden">
        {[1, 2, 3, 4, 5, 6].map((i) => (
          <SkeletonRow key={i} />
        ))}
      </div>
    </div>
  );
}

/**
 * Properties page skeleton:
 * - 2-3 property cards with photo placeholder + details
 */
export function PropertiesSkeleton() {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 animate-in fade-in duration-200" data-testid="properties-skeleton">
      {[1, 2, 3].map((i) => (
        <div key={i} className="card p-5 flex flex-col justify-between space-y-4">
          <SkeletonBox className="h-44 w-full rounded-xl" />
          <div className="space-y-2">
            <SkeletonBox className="h-5 w-44" />
            <SkeletonBox className="h-3.5 w-60 max-w-[80%]" />
          </div>
          <div className="pt-2 border-t border-[var(--border)] flex justify-between items-center">
            <SkeletonBox className="h-4 w-24" />
            <SkeletonBox className="h-4 w-20" />
          </div>
        </div>
      ))}
    </div>
  );
}

/**
 * Rent Roll page skeleton:
 * - Table card with 4-5 rows
 */
export function RentRollSkeleton() {
  return (
    <div className="card overflow-hidden divide-y divide-[var(--border)] animate-in fade-in duration-200" data-testid="rent-roll-skeleton">
      {[1, 2, 3, 4, 5].map((i) => (
        <div key={i} className="p-4 sm:p-5 flex items-center justify-between gap-4">
          <div className="space-y-2 flex-1">
            <SkeletonBox className="h-5 w-48" />
            <SkeletonBox className="h-3.5 w-64 max-w-[70%]" />
          </div>
          <div className="shrink-0 flex items-center gap-3">
            <SkeletonBox className="h-5 w-20" />
            <SkeletonBox className="h-8 w-24 rounded-lg" />
          </div>
        </div>
      ))}
    </div>
  );
}

/**
 * Reusable Data Fetching Error State
 */
export function DataErrorState({ message = "Failed to load data", onRetry, className = "" }) {
  return (
    <div className={`card p-8 text-center rounded-2xl border border-red-500/20 bg-red-500/5 my-6 ${className}`}>
      <div className="w-12 h-12 rounded-xl bg-red-500/10 text-red-500 flex items-center justify-center mx-auto mb-3">
        <AlertCircle size={24} />
      </div>
      <h4 className="text-base font-bold text-[var(--text)] mb-1">Unable to Load Data</h4>
      <p className="text-xs text-[var(--muted)] max-w-md mx-auto mb-4">{message}</p>
      {onRetry && (
        <button
          type="button"
          onClick={onRetry}
          className="btn-secondary text-xs py-2 px-4 inline-flex items-center gap-2 mx-auto"
        >
          <RefreshCw size={14} />
          <span>Tap to retry</span>
        </button>
      )}
    </div>
  );
}
